import React, { useEffect, useState } from "react";
import { Button, Modal, Space, Typography, theme } from "antd";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import type {
  ChangedAnswer,
  ChangedPrompt,
  ChangedReason,
  ChangedReasons,
  ReviewNoteCounts,
} from "./hubTypes";

const { Paragraph, Text } = Typography;

const MONTHS = [
  "ene",
  "feb",
  "mar",
  "abr",
  "may",
  "jun",
  "jul",
  "ago",
  "sep",
  "oct",
  "nov",
  "dic",
];

// «3-oct-2026», in local time; «—» when the platform does not know it.
export function shortDate(iso: string | null): string {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  return `${date.getDate()}-${MONTHS[date.getMonth()]}-${date.getFullYear()}`;
}

// SCRUM-1532: what an upload check or a 409 says about why the OD changed, for
// the question. Nothing when it does not say: the question is then today's.
export const changedReasons = (
  source: ChangedReasons | undefined,
): ChangedReasons =>
  source?.reasons
    ? {
        reasons: source.reasons,
        lastDownloadAt: source.lastDownloadAt ?? null,
        reviewNotes: source.reviewNotes ?? { images: 0, quizQuestions: 0 },
      }
    : {};

// «2 en imágenes y 1 en preguntas de quiz», without the kinds that have none.
const noteCounts = ({ images, quizQuestions }: ReviewNoteCounts): string =>
  [
    images > 0 ? `${images} en imágenes` : null,
    quizQuestions > 0 ? `${quizQuestions} en preguntas de quiz` : null,
  ]
    .filter(Boolean)
    .join(" y ");

const reasonLine = (
  reason: ChangedReason,
  notes: ReviewNoteCounts | undefined,
): string => {
  if (reason === "PLATFORM_EDITS") {
    return "se editó en plataforma después de su última descarga o subida (Sincronizada: No).";
  }
  const counts = notes ? noteCounts(notes) : "";
  return `tiene comentarios de revisión sin resolver${counts ? `: ${counts}` : ""}.`;
};

interface Props {
  // The question being asked; null when there is none.
  prompt: ChangedPrompt | null;
  onAnswer: (answer: ChangedAnswer) => void;
  // False when the course to download is not known.
  canDownload?: boolean;
}

// SCRUM-1532, screen 3: the course was edited in the platform after its last
// upload, so uploading would lose those edits. «Subir igual» asks once more
// before uploading over them. Shared by the HUB and the Súper Subidor. When
// the OD says why (edits, open review notes), there is one line per reason.
const ChangedInPlatformModal: React.FC<Props> = ({
  prompt,
  onAnswer,
  canDownload = true,
}) => {
  const { token } = theme.useToken();
  const [confirming, setConfirming] = useState(false);

  // Each question starts at its first step.
  useEffect(() => setConfirming(false), [prompt]);

  if (!prompt) return null;
  const course =
    [prompt.clave, prompt.title].filter(Boolean).join(" · ") || "El curso";
  const replaced =
    prompt.part === "quiz"
      ? "El quiz de plataforma se sustituye por el de tu YAML."
      : "La OD de plataforma se sustituye por la de tu doc.";
  const reasons = prompt.reasons?.length ? prompt.reasons : null;
  // Open review notes are the only reason: there are no edits to lose.
  const notesOnly =
    reasons !== null && reasons.every((reason) => reason === "REVIEW_NOTES");
  const dates = [
    `Última subida: ${shortDate(prompt.lastUploadAt)}`,
    ...(reasons
      ? [`Última descarga: ${shortDate(prompt.lastDownloadAt ?? null)}`]
      : []),
    `Última edición en plataforma: ${shortDate(prompt.lastPlatformEditAt)}`,
  ].join(" · ");

  // A choice is always made with a button: closing it would leave the part
  // without an answer.
  const common = {
    open: true,
    closable: false,
    maskClosable: false,
    keyboard: false,
    width: 560,
  };

  if (confirming) {
    return (
      <Modal
        {...common}
        title="¿Subir igual?"
        footer={[
          <Button key="back" onClick={() => setConfirming(false)}>
            Volver
          </Button>,
          <Button
            key="force"
            type="primary"
            danger
            onClick={() => onAnswer("force")}
          >
            Subir y perder los cambios
          </Button>,
        ]}
      >
        <Paragraph>
          <Text strong>
            {notesOnly
              ? "Los comentarios de revisión no se pasan a tu doc."
              : "Perderás los cambios introducidos en la plataforma."}
          </Text>{" "}
          {replaced}
        </Paragraph>
      </Modal>
    );
  }

  return (
    <Modal
      {...common}
      title={
        <Space>
          <ExclamationCircleOutlined style={{ color: token.colorWarning }} />
          <span>
            {notesOnly
              ? "Este curso tiene comentarios de revisión"
              : "Este curso cambió en plataforma"}
          </span>
        </Space>
      }
      footer={[
        <Button key="cancel" onClick={() => onAnswer("cancel")}>
          Cancelar
        </Button>,
        <Button key="force" onClick={() => setConfirming(true)}>
          Subir igual
        </Button>,
        ...(canDownload
          ? [
              <Button
                key="download"
                type="primary"
                onClick={() => onAnswer("download")}
              >
                Descargar versión de plataforma
              </Button>,
            ]
          : []),
      ]}
    >
      {reasons ? (
        reasons.map((reason) => (
          <Paragraph key={reason}>
            <Text strong>{course}</Text>{" "}
            {reasonLine(reason, prompt.reviewNotes)}
          </Paragraph>
        ))
      ) : (
        <Paragraph>
          <Text strong>{course}</Text> se editó en plataforma después de su
          última subida.
        </Paragraph>
      )}
      <Paragraph>
        Descarga primero la versión de plataforma y pasa a ese documento, a
        mano, los cambios de tu doc. Después sube el documento descargado.
      </Paragraph>
      <div
        style={{
          background: token.colorFillAlter,
          borderRadius: token.borderRadius,
          padding: "8px 12px",
        }}
      >
        <Text type="secondary">{dates}</Text>
      </div>
    </Modal>
  );
};

export default ChangedInPlatformModal;
